import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip, TextField, InputAdornment } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RemoveIcon from "@material-ui/icons/Remove";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import TableChartOutlinedIcon from "@material-ui/icons/TableChartOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { toast } from "react-toastify";

import FlutuantesContext, { DADOS_CALC_INICIAIS } from "../../context/Flutuantes/FlutuantesContext";
import useArrastavel, { limitarNaTela } from "../../hooks/useArrastavel";
import { fundoNavy, fundoDe, VERDE, campoNavy } from "../../utils/estiloNavy";
import {
  calcularTabela,
  lerNumero,
  mascaraDinheiro,
  moeda,
  taxaMensalEfetiva,
  vencimento,
} from "../../utils/amortizacao";

export const IconeCalculadora = ({ size = 20, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
    aria-hidden="true"
  >
    <rect x="4" y="2" width="16" height="20" rx="3" />
    <path d="M8 6h8" />
    <path d="M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M8 18h.01M12 18h.01" />
    <path d="M16 14v4" />
  </svg>
);

const LARGURA_PADRAO = 600;
const ALTURA_PADRAO = 470; // só para posicionar; sem redimensionar a altura é automática
const MIN_L = 440;
const MIN_A = 360;
const BOLINHA = 58;

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(255,255,255,.09)" : "#e2e8f0";
  const fundoSuave = escuro ? "rgba(255,255,255,.04)" : "#f8fafc";
  const texto = theme.palette.text.primary;
  const textoFraco = escuro ? "#94a3b8" : "#64748b";
  return {
    janela: {
      position: "fixed",
      zIndex: 1350,
      display: "flex",
      flexDirection: "column",
      borderRadius: 20,
      overflow: "hidden",
      background: escuro ? "#131917" : "#fff",
      border: `1px solid ${escuro ? "rgba(148,163,184,.16)" : "rgba(15,23,42,.08)"}`,
      boxShadow: "0 28px 70px rgba(2,6,23,.38)",
      animation: "iaSubir .25s ease backwards",
    },
    topo: {
      background: fundoDe(theme),
      color: "#fff",
      padding: "12px 12px 12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      cursor: "grab",
      flexShrink: 0,
      "&:active": { cursor: "grabbing" },
    },
    topoIcone: {
      width: 34,
      height: 34,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: `linear-gradient(135deg, #34d399, ${VERDE})`,
      boxShadow: "0 6px 16px rgba(30,152,85,.4)",
      flexShrink: 0,
    },
    topoTitulo: { fontWeight: 800, fontSize: "0.95rem", lineHeight: 1.2 },
    topoSub: { fontSize: "0.72rem", color: "#94a3b8" },
    topoBotao: {
      color: "#fff",
      width: 30,
      height: 30,
      borderRadius: 9,
      background: "rgba(255,255,255,.08)",
      "&:hover": { background: "rgba(255,255,255,.18)" },
    },
    corpo: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: 14,
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },

    // ── Formulário ──
    grade: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      alignItems: "end",
    },
    gradeEstreita: { gridTemplateColumns: "1fr" },
    rotulo: {
      fontSize: "0.66rem",
      fontWeight: 800,
      letterSpacing: ".06em",
      textTransform: "uppercase",
      color: textoFraco,
      marginBottom: 5,
      display: "block",
    },
    campo: {
      ...campoNavy(theme),
      width: "100%",
      "& .MuiOutlinedInput-root": {
        ...campoNavy(theme)["& .MuiOutlinedInput-root"],
        height: 40,
        fontWeight: 600,
      },
      "& .MuiInputAdornment-root p": { fontSize: "0.8rem", fontWeight: 700, color: textoFraco },
    },
    segmento: {
      display: "flex",
      height: 40,
      padding: 3,
      borderRadius: 12,
      background: fundoSuave,
      border: `1px solid ${borda}`,
      gap: 3,
    },
    segmentoOpcao: {
      flex: 1,
      border: "none",
      borderRadius: 9,
      background: "transparent",
      color: textoFraco,
      fontFamily: "inherit",
      fontSize: "0.8rem",
      fontWeight: 700,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "all .15s ease",
      "&:hover": { color: texto },
    },
    segmentoAtivo: {
      background: escuro ? "rgba(30,152,85,.22)" : "#fff",
      color: `${escuro ? "#4ade80" : VERDE} !important`,
      boxShadow: escuro ? "none" : "0 2px 6px rgba(15,23,42,.1)",
    },
    linhaTaxa: { display: "flex", gap: 8 },

    // ── Vencimento ──
    vencBotao: {
      width: "100%",
      height: 40,
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "0 12px",
      borderRadius: 12,
      border: `1px solid ${borda}`,
      background: escuro ? "rgba(255,255,255,0.03)" : "#fff",
      color: texto,
      fontFamily: "inherit",
      fontSize: "0.9rem",
      fontWeight: 600,
      cursor: "pointer",
      textAlign: "left",
      "&:hover": { borderColor: VERDE },
      "& svg": { fontSize: 18, color: textoFraco },
    },
    vencBotaoAberto: { borderColor: VERDE, boxShadow: "0 0 0 4px rgba(30,152,85,0.12)" },
    vencVazio: { color: textoFraco, fontWeight: 500 },
    seletorMes: {
      gridColumn: "1 / -1",
      borderRadius: 14,
      border: `1px solid ${borda}`,
      background: fundoSuave,
      padding: 10,
      animation: "iaSubir .18s ease backwards",
    },
    seletorTopo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
      fontWeight: 800,
      color: texto,
    },
    seletorSeta: { width: 28, height: 28, color: texto },
    seletorGrade: { display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6 },
    mes: {
      height: 32,
      border: `1px solid ${borda}`,
      borderRadius: 9,
      background: escuro ? "rgba(255,255,255,.03)" : "#fff",
      color: texto,
      fontFamily: "inherit",
      fontSize: "0.8rem",
      fontWeight: 600,
      cursor: "pointer",
      textTransform: "capitalize",
      "&:hover": { borderColor: VERDE, color: VERDE },
    },
    mesAtivo: {
      background: `linear-gradient(135deg, #22c55e, ${VERDE}) !important`,
      borderColor: "transparent !important",
      color: "#fff !important",
      boxShadow: "0 6px 14px rgba(30,152,85,.3)",
    },
    seletorRodape: { display: "flex", justifyContent: "space-between", marginTop: 8 },
    linkBotao: {
      border: "none",
      background: "transparent",
      color: VERDE,
      fontFamily: "inherit",
      fontWeight: 700,
      fontSize: "0.78rem",
      cursor: "pointer",
      padding: "4px 6px",
      borderRadius: 6,
      "&:hover": { background: "rgba(30,152,85,.1)" },
    },

    // ── Resultado ──
    resumo: { display: "grid", gridTemplateColumns: "1.25fr 1fr 1fr", gap: 8 },
    card: {
      borderRadius: 14,
      padding: "10px 12px",
      background: fundoSuave,
      border: `1px solid ${borda}`,
      minWidth: 0,
    },
    cardParcela: {
      background: `linear-gradient(135deg, #22c55e, ${VERDE})`,
      border: "none",
      color: "#fff",
      boxShadow: "0 10px 22px rgba(30,152,85,.3)",
    },
    cardRotulo: {
      fontSize: "0.62rem",
      fontWeight: 800,
      letterSpacing: ".06em",
      textTransform: "uppercase",
      opacity: 0.85,
    },
    cardValor: {
      fontSize: "1.08rem",
      fontWeight: 800,
      marginTop: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    // ── Ações ──
    acoes: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    acao: {
      height: 36,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "0 14px",
      borderRadius: 10,
      border: `1px solid ${borda}`,
      background: escuro ? "rgba(255,255,255,.03)" : "#fff",
      color: texto,
      fontFamily: "inherit",
      fontSize: "0.8rem",
      fontWeight: 700,
      cursor: "pointer",
      transition: "all .15s ease",
      "& svg": { fontSize: 17 },
      "&:hover:not(:disabled)": { borderColor: VERDE, color: VERDE },
      "&:disabled": { opacity: 0.45, cursor: "default" },
    },
    acaoPrincipal: {
      border: "none",
      color: "#fff !important",
      background: `linear-gradient(135deg, #22c55e, ${VERDE})`,
      boxShadow: "0 6px 14px rgba(30,152,85,.28)",
      "&:hover:not(:disabled)": { filter: "brightness(1.06)" },
    },
    acaoAtiva: { borderColor: VERDE, color: VERDE, background: "rgba(30,152,85,.08)" },
    acaoTexto: { border: "none", background: "transparent", marginLeft: "auto", color: textoFraco },

    // ── Tabela ──
    tabela: {
      flex: 1,
      minHeight: 150,
      overflowY: "auto",
      borderRadius: 12,
      border: `1px solid ${borda}`,
      "& table": { width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" },
      "& th": {
        position: "sticky",
        top: 0,
        zIndex: 1,
        background: escuro ? "#1d2522" : "#f1f5f9",
        color: textoFraco,
        textAlign: "right",
        padding: "8px 10px",
        fontWeight: 800,
        fontSize: "0.64rem",
        letterSpacing: ".05em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      },
      "& td": {
        textAlign: "right",
        padding: "7px 10px",
        borderTop: `1px solid ${escuro ? "rgba(255,255,255,.05)" : "#f1f5f9"}`,
        whiteSpace: "nowrap",
        color: texto,
      },
      "& tbody tr:nth-child(even) td": { background: escuro ? "rgba(255,255,255,.02)" : "#fbfdff" },
      "& tbody tr:hover td": { background: "rgba(30,152,85,.07)" },
      "& th:nth-child(-n+2), & td:nth-child(-n+2)": { textAlign: "left" },
    },

    // ── Redimensionar ──
    puxadorDireita: {
      position: "absolute",
      top: 60,
      right: 0,
      bottom: 16,
      width: 8,
      cursor: "ew-resize",
      zIndex: 2,
    },
    puxadorBaixo: {
      position: "absolute",
      left: 16,
      right: 16,
      bottom: 0,
      height: 8,
      cursor: "ns-resize",
      zIndex: 2,
    },
    puxadorCanto: {
      position: "absolute",
      right: 2,
      bottom: 2,
      width: 18,
      height: 18,
      cursor: "nwse-resize",
      zIndex: 3,
      color: textoFraco,
      opacity: 0.7,
      "&:hover": { opacity: 1, color: VERDE },
    },

    bolinha: {
      position: "fixed",
      zIndex: 1350,
      width: BOLINHA,
      height: BOLINHA,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      cursor: "grab",
      background: `linear-gradient(135deg, #34d399, ${VERDE})`,
      boxShadow: "0 12px 28px rgba(30,152,85,.45), 0 0 0 4px rgba(30,152,85,.15)",
      animation: "iaSubir .25s ease backwards",
      transition: "transform .15s ease",
      "&:active": { cursor: "grabbing" },
      "&:hover": { transform: "scale(1.05)" },
    },
  };
});

const Segmento = ({ classes, opcoes, valor, onChange }) => (
  <div className={classes.segmento}>
    {opcoes.map((o) => (
      <button
        key={o.valor}
        type="button"
        className={`${classes.segmentoOpcao} ${valor === o.valor ? classes.segmentoAtivo : ""}`}
        onClick={() => onChange(o.valor)}
      >
        {o.rotulo}
      </button>
    ))}
  </div>
);

const SeletorMes = ({ classes, valor, onEscolher }) => {
  const [anoSel, mesSel] = /^\d{4}-\d{2}$/.test(valor || "") ? valor.split("-").map(Number) : [null, null];
  const hoje = new Date();
  const [ano, setAno] = useState(anoSel || hoje.getFullYear());
  const doisDig = (n) => String(n).padStart(2, "0");
  return (
    <div className={classes.seletorMes}>
      <div className={classes.seletorTopo}>
        <IconButton size="small" className={classes.seletorSeta} onClick={() => setAno((a) => a - 1)}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        {ano}
        <IconButton size="small" className={classes.seletorSeta} onClick={() => setAno((a) => a + 1)}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </div>
      <div className={classes.seletorGrade}>
        {MESES.map((m, i) => (
          <button
            key={m}
            type="button"
            className={`${classes.mes} ${anoSel === ano && mesSel === i + 1 ? classes.mesAtivo : ""}`}
            onClick={() => onEscolher(`${ano}-${doisDig(i + 1)}`)}
          >
            {m}
          </button>
        ))}
      </div>
      <div className={classes.seletorRodape}>
        <button type="button" className={classes.linkBotao} onClick={() => onEscolher("")}>
          Limpar
        </button>
        <button
          type="button"
          className={classes.linkBotao}
          onClick={() => onEscolher(`${hoje.getFullYear()}-${doisDig(hoje.getMonth() + 1)}`)}
        >
          Este mês
        </button>
      </div>
    </div>
  );
};

const nomeDoMes = (v) => {
  if (!/^\d{4}-\d{2}$/.test(v || "")) return "";
  const [a, m] = v.split("-").map(Number);
  return `${MESES[m - 1]}/${a}`;
};

const CalculadoraFlutuante = () => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const { calc, atualizarCalculadora, minimizarCalculadora, restaurarCalculadora, fecharCalculadora } =
    useContext(FlutuantesContext);
  const [verTabela, setVerTabela] = useState(false);
  const [escolhendoMes, setEscolhendoMes] = useState(false);
  const janelaRef = useRef(null);
  const [tamanhoAoVivo, setTamanhoAoVivo] = useState(null); // enquanto estica

  // Presa à conversa: saiu da conversa onde foi aberta, ela fecha.
  const naConversa = !!calc && pathname === `/atendimentos/${calc.ticketId}`;
  useEffect(() => {
    if (calc && !naConversa) fecharCalculadora();
  }, [calc, naConversa, fecharCalculadora]);

  const dados = calc?.dados || DADOS_CALC_INICIAIS;
  const setDado = (campo, valor) => atualizarCalculadora({ dados: { ...dados, [campo]: valor } });

  // Tamanho: largura sempre fixa; altura só fica fixa depois que o atendente estica.
  const tamanho = tamanhoAoVivo || calc?.tamanho;
  const largura = Math.min(tamanho?.w || LARGURA_PADRAO, window.innerWidth - 16);
  const alturaFixa = tamanho?.h ? Math.min(tamanho.h, window.innerHeight - 16) : null;
  const alturaRef = alturaFixa || ALTURA_PADRAO;

  const posJanela = useMemo(
    () =>
      limitarNaTela(
        calc?.pos || { x: (window.innerWidth - largura) / 2, y: (window.innerHeight - alturaRef) / 2 },
        largura,
        Math.min(alturaRef, window.innerHeight - 16)
      ),
    [calc?.pos, largura, alturaRef]
  );
  const posBolinha = useMemo(
    () =>
      calc?.posBolinha ||
      limitarNaTela({ x: window.innerWidth - BOLINHA - 28, y: window.innerHeight - 220 }, BOLINHA, BOLINHA),
    [calc?.posBolinha]
  );

  const arrastarJanela = useArrastavel({
    pos: posJanela,
    largura,
    altura: Math.min(alturaRef, window.innerHeight - 16),
    onMover: (p) => atualizarCalculadora({ pos: p }),
  });
  const arrastarBolinha = useArrastavel({
    pos: posBolinha,
    largura: BOLINHA,
    altura: BOLINHA,
    onMover: (p) => atualizarCalculadora({ posBolinha: p }),
    onClique: restaurarCalculadora,
  });

  // Esticar pela borda direita, de baixo ou pelo canto.
  const iniciarRedimensionar = useCallback(
    (direcao) => (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const caixa = janelaRef.current?.getBoundingClientRect();
      if (!caixa) return;
      const inicio = { x: e.clientX, y: e.clientY, w: caixa.width, h: caixa.height };
      let ultimo = null;
      let quadro = null;
      const mover = (ev) => {
        const maxW = window.innerWidth - caixa.left - 8;
        const maxH = window.innerHeight - caixa.top - 8;
        const novo = {
          w: direcao === "baixo" ? inicio.w : Math.max(MIN_L, Math.min(maxW, inicio.w + ev.clientX - inicio.x)),
          h: direcao === "direita" ? inicio.h : Math.max(MIN_A, Math.min(maxH, inicio.h + ev.clientY - inicio.y)),
        };
        ultimo = novo;
        if (quadro) return;
        quadro = requestAnimationFrame(() => {
          quadro = null;
          setTamanhoAoVivo(ultimo);
        });
      };
      const soltar = () => {
        window.removeEventListener("pointermove", mover);
        window.removeEventListener("pointerup", soltar);
        if (quadro) cancelAnimationFrame(quadro);
        if (ultimo) atualizarCalculadora({ tamanho: ultimo });
        setTamanhoAoVivo(null);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };
      document.body.style.userSelect = "none";
      document.body.style.cursor =
        direcao === "direita" ? "ew-resize" : direcao === "baixo" ? "ns-resize" : "nwse-resize";
      window.addEventListener("pointermove", mover);
      window.addEventListener("pointerup", soltar);
    },
    [atualizarCalculadora]
  );

  const valor = lerNumero(dados.valor);
  const taxa = taxaMensalEfetiva(dados.taxa, dados.tipoTaxa);
  const resultado = useMemo(
    () => calcularTabela({ valor, parcelas: dados.parcelas, taxa, sistema: dados.sistema }),
    [valor, dados.parcelas, taxa, dados.sistema]
  );

  if (!calc || !naConversa) return null;

  if (calc.minimizada) {
    return (
      <div
        className={classes.bolinha}
        style={{ left: arrastarBolinha.pos.x, top: arrastarBolinha.pos.y, ...arrastarBolinha.style }}
        onPointerDown={arrastarBolinha.onPointerDown}
        role="button"
        aria-label="Abrir calculadora"
        title="Calculadora de amortização (clique para abrir, arraste para mover)"
      >
        <IconeCalculadora size={26} />
      </div>
    );
  }

  const ehSac = dados.sistema === "sac";
  const estreita = largura < 520;

  const copiarResumo = () => {
    if (!resultado) return;
    const n = parseInt(dados.parcelas, 10);
    const parcelaTxt = ehSac
      ? `${n}x de ${moeda(resultado.primeira)} a ${moeda(resultado.ultima)} (SAC)`
      : `${n}x de ${moeda(resultado.primeira)}`;
    const texto =
      `*Simulação*\n` +
      `Valor: ${moeda(valor)}\n` +
      `Parcelas: ${parcelaTxt}\n` +
      `Total a pagar: ${moeda(resultado.totalPagar)}\n` +
      `Total de juros: ${moeda(resultado.totalJuros)}\n` +
      `_Valores aproximados. O valor oficial é o do contrato._`;
    navigator.clipboard
      ?.writeText(texto)
      .then(() => toast.success("Resumo copiado. É só colar na conversa."))
      .catch(() => toast.error("Não consegui copiar."));
  };

  const salvarPdf = () => {
    if (!resultado) return;

    const n = parseInt(dados.parcelas, 10);
    const sistemaTxt = ehSac ? "SAC - parcelas decrescentes" : "Price - parcelas fixas";
    const taxaTxt = `${lerNumero(dados.taxa).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })}% ${dados.tipoTaxa === "anual" ? "a.a." : "a.m."}`;
    const parcelaTxt = ehSac
      ? `${moeda(resultado.primeira)} -> ${moeda(resultado.ultima)}`
      : moeda(resultado.primeira);

    const linhas = resultado.linhas
      .map(
        (l) =>
          `<tr><td>${l.n}</td><td>${vencimento(dados.inicio, l.n)}</td>` +
          `<td class="d">${moeda(l.parcela)}</td><td class="d">${moeda(l.amort)}</td>` +
          `<td class="d">${moeda(l.juros)}</td><td class="d">${moeda(l.anterior)}</td>` +
          `<td class="d b">${moeda(l.saldo)}</td></tr>`
      )
      .join("");

    const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<title>Simulacao de amortizacao</title><style>
  @page { size: A4 portrait; margin: 14mm; }
  * { font-family: Arial, Helvetica, sans-serif; }
  body { margin: 0; color: #111; }
  h1 { font-size: 17px; margin: 0 0 2px; }
  .sub { font-size: 11px; color: #666; margin-bottom: 14px; }
  .resumo { display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
  .card { border: 1px solid #ddd; border-radius: 6px; padding: 8px 12px; min-width: 130px; }
  .card small { display: block; font-size: 9px; text-transform: uppercase;
                letter-spacing: .4px; color: #666; margin-bottom: 3px; }
  .card strong { font-size: 13px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th { background: #f1f5f9; text-align: right; padding: 6px 7px;
       border-bottom: 1px solid #cbd5e1; font-size: 9px; text-transform: uppercase; }
  th:first-child, th:nth-child(2), td:first-child, td:nth-child(2) { text-align: left; }
  td { padding: 5px 7px; border-bottom: 1px solid #eee; }
  td.d { text-align: right; }
  td.b { font-weight: bold; }
  tbody tr:nth-child(even) { background: #fafafa; }
  .rodape { margin-top: 12px; font-size: 9px; color: #666; font-style: italic; }
</style></head><body>
<h1>Simulacao de amortizacao</h1>
<div class="sub">${sistemaTxt} &middot; ${n}x &middot; taxa ${taxaTxt} &middot; gerado em ${new Date().toLocaleDateString("pt-BR")}</div>
<div class="resumo">
  <div class="card"><small>Valor solicitado</small><strong>${moeda(valor)}</strong></div>
  <div class="card"><small>${ehSac ? "1a -> ultima parcela" : "Valor da parcela"}</small><strong>${parcelaTxt}</strong></div>
  <div class="card"><small>Total a pagar</small><strong>${moeda(resultado.totalPagar)}</strong></div>
  <div class="card"><small>Total em juros</small><strong>${moeda(resultado.totalJuros)}</strong></div>
</div>
<table><thead><tr><th>N&ordm;</th><th>Vencimento</th><th>Parcela</th><th>Amortiza&ccedil;&atilde;o</th>
<th>Juros</th><th>Saldo anterior</th><th>Saldo devedor</th></tr></thead>
<tbody>${linhas}</tbody></table>
<div class="rodape">Valores aproximados. O valor oficial e o do contrato.</div>
</body></html>`;

    const janela = window.open("", "_blank");
    if (!janela) {
      toast.error("O navegador bloqueou a janela. Libere o pop-up e tente de novo.");
      return;
    }
    janela.document.write(html);
    janela.document.close();
    janela.focus();
    // Espera o layout fechar antes de abrir a caixa de impressao.
    setTimeout(() => janela.print(), 400);
  };

  return (
    <div
      ref={janelaRef}
      className={classes.janela}
      style={{
        left: arrastarJanela.pos.x,
        top: arrastarJanela.pos.y,
        width: largura,
        height: alturaFixa || "auto",
        maxHeight: "calc(100vh - 16px)",
      }}
    >
      <div className={classes.topo} onPointerDown={arrastarJanela.onPointerDown} style={arrastarJanela.style}>
        <div className={classes.topoIcone}>
          <IconeCalculadora size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.topoTitulo}>Calculadora de Amortização</div>
          <div className={classes.topoSub}>Arraste pelo topo · estique pelas bordas</div>
        </div>
        <Tooltip title="Minimizar">
          <IconButton className={classes.topoBotao} onClick={minimizarCalculadora}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fechar">
          <IconButton className={classes.topoBotao} onClick={fecharCalculadora}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.corpo}>
        <div className={`${classes.grade} ${estreita ? classes.gradeEstreita : ""}`}>
          <div>
            <span className={classes.rotulo}>Valor solicitado</span>
            <TextField
              className={classes.campo}
              variant="outlined"
              size="small"
              placeholder="0,00"
              value={dados.valor}
              onChange={(e) => setDado("valor", mascaraDinheiro(e.target.value))}
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              autoFocus
            />
          </div>
          <div>
            <span className={classes.rotulo}>Sistema de amortização</span>
            <Segmento
              classes={classes}
              valor={dados.sistema}
              onChange={(v) => setDado("sistema", v)}
              opcoes={[
                { valor: "price", rotulo: "Price · fixas" },
                { valor: "sac", rotulo: "SAC · decrescentes" },
              ]}
            />
          </div>
          <div>
            <span className={classes.rotulo}>Parcelas</span>
            <TextField
              className={classes.campo}
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ min: 1, max: 600 }}
              value={dados.parcelas}
              onChange={(e) => setDado("parcelas", e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">meses</InputAdornment> }}
            />
          </div>
          <div>
            <span className={classes.rotulo}>Taxa de juros</span>
            <div className={classes.linhaTaxa}>
              <TextField
                className={classes.campo}
                variant="outlined"
                size="small"
                placeholder="1,63"
                value={dados.taxa}
                onChange={(e) => setDado("taxa", e.target.value.replace(/[^\d,.]/g, ""))}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                style={{ flex: 1 }}
              />
              <div style={{ width: 110 }}>
                <Segmento
                  classes={classes}
                  valor={dados.tipoTaxa}
                  onChange={(v) => setDado("tipoTaxa", v)}
                  opcoes={[
                    { valor: "mensal", rotulo: "a.m." },
                    { valor: "anual", rotulo: "a.a." },
                  ]}
                />
              </div>
            </div>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <span className={classes.rotulo}>1º vencimento (opcional)</span>
            <button
              type="button"
              className={`${classes.vencBotao} ${escolhendoMes ? classes.vencBotaoAberto : ""}`}
              onClick={() => setEscolhendoMes((v) => !v)}
            >
              <EventOutlinedIcon />
              <span style={{ flex: 1, textTransform: "capitalize" }}>
                {nomeDoMes(dados.inicio) || <span className={classes.vencVazio}>Escolher mês</span>}
              </span>
              <ExpandMoreIcon
                style={{ transform: escolhendoMes ? "rotate(180deg)" : "none", transition: "transform .2s" }}
              />
            </button>
          </div>
          {escolhendoMes && (
            <SeletorMes
              classes={classes}
              valor={dados.inicio}
              onEscolher={(v) => {
                setDado("inicio", v);
                setEscolhendoMes(false);
              }}
            />
          )}
        </div>

        <div className={classes.resumo} style={estreita ? { gridTemplateColumns: "1fr" } : undefined}>
          <div className={`${classes.card} ${classes.cardParcela}`}>
            <div className={classes.cardRotulo}>{ehSac ? "1ª → última parcela" : "Valor da parcela"}</div>
            <div className={classes.cardValor}>
              {!resultado
                ? "R$ 0,00"
                : ehSac
                ? `${moeda(resultado.primeira)} → ${moeda(resultado.ultima)}`
                : moeda(resultado.primeira)}
            </div>
          </div>
          <div className={classes.card} style={{ borderLeft: "3px solid #ef4444" }}>
            <div className={classes.cardRotulo}>Total a pagar</div>
            <div className={classes.cardValor} style={{ color: "#ef4444" }}>
              {moeda(resultado?.totalPagar || 0)}
            </div>
          </div>
          <div className={classes.card} style={{ borderLeft: "3px solid #f59e0b" }}>
            <div className={classes.cardRotulo}>Total em juros</div>
            <div className={classes.cardValor} style={{ color: "#f59e0b" }}>
              {moeda(resultado?.totalJuros || 0)}
            </div>
          </div>
        </div>

        <div className={classes.acoes}>
          <button
            type="button"
            className={`${classes.acao} ${classes.acaoPrincipal}`}
            disabled={!resultado}
            onClick={copiarResumo}
          >
            <FileCopyOutlinedIcon /> Copiar resumo
          </button>
          <button
            type="button"
            className={classes.acao}
            disabled={!resultado}
            onClick={salvarPdf}
          >
            <PictureAsPdfOutlinedIcon /> Salvar PDF
          </button>
          <button
            type="button"
            className={`${classes.acao} ${verTabela ? classes.acaoAtiva : ""}`}
            disabled={!resultado}
            onClick={() => setVerTabela((v) => !v)}
          >
            <TableChartOutlinedIcon /> {verTabela ? "Esconder tabela" : "Ver tabela"}
          </button>
          <button
            type="button"
            className={`${classes.acao} ${classes.acaoTexto}`}
            onClick={() => atualizarCalculadora({ dados: { ...DADOS_CALC_INICIAIS } })}
          >
            <RefreshIcon /> Limpar
          </button>
        </div>

        {verTabela && resultado && (
          <div className={classes.tabela}>
            <table>
              <thead>
                <tr>
                  <th>Nº</th>
                  <th>Vencimento</th>
                  <th>Parcela</th>
                  <th>Amortização</th>
                  <th>Juros</th>
                  <th>Saldo anterior</th>
                  <th>Saldo devedor</th>
                </tr>
              </thead>
              <tbody>
                {resultado.linhas.map((l) => (
                  <tr key={l.n}>
                    <td style={{ fontWeight: 700 }}>{l.n}</td>
                    <td style={{ textTransform: "capitalize" }}>{vencimento(dados.inicio, l.n)}</td>
                    <td style={{ color: VERDE, fontWeight: 800 }}>{moeda(l.parcela)}</td>
                    <td>{moeda(l.amort)}</td>
                    <td style={{ color: "#f59e0b" }}>{moeda(l.juros)}</td>
                    <td>{moeda(l.anterior)}</td>
                    <td style={{ fontWeight: 700 }}>{moeda(l.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Puxadores para esticar */}
      <div className={classes.puxadorDireita} onPointerDown={iniciarRedimensionar("direita")} />
      <div className={classes.puxadorBaixo} onPointerDown={iniciarRedimensionar("baixo")} />
      <div
        className={classes.puxadorCanto}
        onPointerDown={iniciarRedimensionar("canto")}
        onDoubleClick={() => atualizarCalculadora({ tamanho: null })}
        title="Arraste para esticar · dois cliques volta ao tamanho normal"
      >
        <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
          <path d="M16 6L6 16M16 11l-5 5M16 15.5l-.5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
};

export default CalculadoraFlutuante;
